/**
 * BHABY HEALTH CONNECT — Main Application
 * Responsive layout with role-based navigation for Patient, Doctor, Nurse, and Admin portals.
 */

import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, User, Users, Stethoscope, ClipboardList,
  TestTube, Bell, Settings, LogOut, Menu, X, Shield, HeartPulse,
  FileText, Activity, Ambulance, ChevronRight, Loader2,
  Building2, CreditCard, AlertTriangle, Pill, Eye, EyeOff, KeyRound, UserPlus,
  CheckCircle, Mail, Clock, FlaskConical, Wallet, MessageSquare, MessageSquareWarning
} from 'lucide-react';

// Services
import { authApi } from './services/api';

// Shared Components
import NotificationBell from './components/shared/NotificationBell';
import IncomingCallOverlay from './components/shared/IncomingCallOverlay';
import { useIncomingCall } from './components/shared/useIncomingCall';
import LanguageSwitcher from './components/shared/LanguageSwitcher';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import BrandLogo from './components/shared/BrandLogo';
import ErrorBoundary from './components/shared/ErrorBoundary';
import DoctorVerificationGate from './components/shared/DoctorVerificationGate';
import CookieConsent from './components/shared/CookieConsent';
import { PermissionProvider, usePermissions } from './i18n/PermissionContext';
import { Link as RouterLink } from 'react-router-dom';

// ========== Auth Context ==========
interface AuthUser {
  uid: string;
  name: string;
  role: 'patient' | 'doctor' | 'nurse' | 'admin';
  token: string;
}

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

// ========== Navigation Config ==========
interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const NAV_CONFIG: Record<string, (NavItem & { labelKey: string })[]> = {
  patient: [
    { path: '/patient/dashboard', label: 'Dashboard', labelKey: 'dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/patient/find-doctor', label: 'Find a Doctor', labelKey: 'findDoctor', icon: <Stethoscope size={20} /> },
    { path: '/patient/bookings', label: 'Appointments', labelKey: 'myBookings', icon: <Calendar size={20} /> },
    { path: '/patient/lab-results', label: 'Lab Results', labelKey: 'labResults', icon: <TestTube size={20} /> },
    { path: '/patient/payments', label: 'Payments', labelKey: 'payments', icon: <CreditCard size={20} /> },
    { path: '/patient/prescriptions', label: 'Prescriptions', labelKey: 'prescriptions', icon: <Pill size={20} /> },
    { path: '/patient/records-access', label: 'My Records', labelKey: 'myRecords', icon: <FileText size={20} /> },
    { path: '/patient/profile', label: 'Profile', labelKey: 'profile', icon: <User size={20} /> },
  ],
  doctor: [
    { path: '/doctor/dashboard', label: 'Dashboard', labelKey: 'dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/doctor/schedule', label: 'Schedule', labelKey: 'schedule', icon: <Calendar size={20} /> },
    { path: '/doctor/pending', label: 'Pending Bookings', labelKey: 'pendingBookings', icon: <Clock size={20} /> },
    { path: '/doctor/consultations', label: 'My Consultations', labelKey: 'myConsultations', icon: <Stethoscope size={20} /> },
    { path: '/doctor/patients', label: 'Patients', labelKey: 'patients', icon: <Users size={20} /> },
    { path: '/doctor/patient-transfer', label: 'Patient Transfer', labelKey: 'patientTransfer', icon: <ChevronRight size={20} /> },
    { path: '/doctor/lab-tests', label: 'Lab Tests', labelKey: 'labTests', icon: <FlaskConical size={20} /> },
    { path: '/doctor/wallet', label: 'Wallet', labelKey: 'wallet', icon: <Wallet size={20} /> },
    { path: '/doctor/messages', label: 'Messages', labelKey: 'messages', icon: <MessageSquare size={20} /> },
    { path: '/doctor/profile', label: 'Profile', labelKey: 'profile', icon: <User size={20} /> },
  ],
  nurse: [
    { path: '/nurse/dashboard', label: 'Dashboard', labelKey: 'dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/nurse/task-management', label: 'My Tasks', labelKey: 'myTasks', icon: <ClipboardList size={20} /> },
    { path: '/nurse/medication', label: 'Medications', labelKey: 'medications', icon: <Pill size={20} /> },
    { path: '/nurse/collections', label: 'Collections', labelKey: 'collections', icon: <TestTube size={20} /> },
    { path: '/nurse/profile', label: 'Profile & Shift', labelKey: 'profileShift', icon: <User size={20} /> },
  ],
  admin: [
    { path: '/admin/dashboard', label: 'Dashboard', labelKey: 'dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/admin/bookings', label: 'All Bookings', labelKey: 'allBookings', icon: <ClipboardList size={20} /> },
    { path: '/admin/pricing', label: 'Pricing & Labs', labelKey: 'pricingLabs', icon: <CreditCard size={20} /> },
    { path: '/admin/users', label: 'Users', labelKey: 'users', icon: <Users size={20} /> },
    { path: '/admin/insurance', label: 'Insurance', labelKey: 'insurance', icon: <Shield size={20} /> },
    { path: '/admin/emergency', label: 'Emergency', labelKey: 'emergency', icon: <Ambulance size={20} /> },
    { path: '/admin/payments', label: 'Payments', labelKey: 'payments', icon: <CreditCard size={20} /> },
    { path: '/admin/withdrawals', label: 'Withdrawals', labelKey: 'withdrawals', icon: <Wallet size={20} /> },
    { path: '/admin/nurse-tasks', label: 'Nurse Tasks', labelKey: 'nurseTasks', icon: <ClipboardList size={20} /> },
    { path: '/admin/doctor-verification', label: 'Doctor Verification', labelKey: 'doctorVerification', icon: <Shield size={20} /> },
    { path: '/admin/nurse-verification', label: 'Nurse Verification', labelKey: 'nurseVerification', icon: <Shield size={20} /> },
    { path: '/admin/lab-orders', label: 'Lab Orders', labelKey: 'labOrdersAdmin', icon: <TestTube size={20} /> },
    { path: '/admin/records-access', label: 'Records Access', labelKey: 'recordsAccess', icon: <Shield size={20} /> },
    { path: '/admin/clinical-review', label: 'Clinical Review', labelKey: 'clinicalReview', icon: <FileText size={20} /> },
    { path: '/admin/feedback', label: 'Feedback', labelKey: 'feedback', icon: <MessageSquareWarning size={20} /> },
    { path: '/admin/monitoring', label: 'System', labelKey: 'monitoring', icon: <Activity size={20} /> },
  ],
};

const MOBILE_PRIMARY_KEYS: Record<string, string[]> = {
  patient: ['dashboard', 'findDoctor', 'myBookings', 'labResults'],
  doctor: ['dashboard', 'schedule', 'pendingBookings', 'myConsultations'],
  nurse: ['dashboard', 'myTasks', 'collections'],
  admin: ['dashboard', 'allBookings', 'users', 'payments'],
};

const MobileDock: React.FC<{ navItems: Array<NavItem & { labelKey: string }>; role: string }> = ({ navItems, role }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const keys = MOBILE_PRIMARY_KEYS[role] || [];
  const primary = keys.map(key => navItems.find(item => item.labelKey === key)).filter(Boolean) as Array<NavItem & { labelKey: string }>;
  return (
    <nav className="mobile-dock" aria-label="Primary mobile navigation">
      {primary.slice(0, 4).map(item => {
        const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
        return (
          <button key={item.path} type="button" className={`mobile-dock__item ${active ? 'is-active' : ''}`} onClick={() => navigate(item.path)} aria-current={active ? 'page' : undefined}>
            <span className="mobile-dock__icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

// ========== Layout ==========
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  const navItems = (user ? NAV_CONFIG[user.role] || [] : []).map(item => ({
    ...item,
    label: t(`nav.${item.labelKey}`) || item.label,
  }));

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const openNavigation = () => setSidebarOpen(true);
    window.addEventListener('bhealth:open-navigation', openNavigation);
    return () => window.removeEventListener('bhealth:open-navigation', openNavigation);
  }, []);

  // Trap focus in sidebar when open on mobile
  useEffect(() => {
    if (sidebarOpen && sidebarRef.current) {
      sidebarRef.current.focus();
    }
  }, [sidebarOpen]);

  // During an active video consultation the shell goes immersive: the mobile
  // dock is collapsed (see `.app-shell.is-immersive-call`) so the call owns the
  // viewport. Navigation is NOT removed — the header menu still opens the
  // sidebar, and the route stays fully reachable.
  const isVideoConsultationRoute = /^\/(patient|doctor)\/video\//.test(location.pathname);

  // Incoming-call ring, application-wide.
  //
  // A consultation is presence-based: nothing used to tell an idle doctor that
  // a patient had opened the call, so a "call" only happened when both people
  // navigated to it. The invite arrives on the shared realtime socket from the
  // server's inviter, so it must be handled HERE — from any screen — not inside
  // the consultation room, which the recipient has not opened. All decisions
  // (validation, idempotency, timeout, routing) live in the hook.
  const incomingCall = useIncomingCall({ uid: user?.uid, role: user?.role });

  return (
    <div className={`app-shell flex h-dvh min-h-0 overflow-hidden bg-surface-50${isVideoConsultationRoute ? ' is-immersive-call' : ''}`}>
      {/* Skip to content (a11y) */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        role="navigation"
        aria-label="Main navigation"
        className={`
          fixed top-0 left-0 z-50 h-dvh flex flex-col transition-transform duration-300 ease-in-out
          w-[16rem] shadow-xl
          lg:translate-x-0 lg:z-20
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          bg-sidebar-bg pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-0
        `}
      >
        {/* Logo — official brand mark */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg p-1">
            <BrandLogo className="w-full h-full" alt="" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-white tracking-tight">BHABY</div>
            <div className="text-[0.6rem] text-primary-300 uppercase tracking-widest font-medium">Health Connect</div>
          </div>
          {/* Close button (mobile) */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close navigation menu"
          >
            <X size={20} className="text-white/60" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-3 sidebar-nav" aria-label={`${user?.role} navigation`}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1
                  text-sm font-medium transition-all duration-150
                  ${isActive
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                    : 'text-sidebar-text hover:bg-white/10 hover:text-white'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="flex-shrink-0 opacity-80">{item.icon}</span>
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Section — fixed at bottom, never scrolls away */}
        <div className="flex-shrink-0 p-3 border-t border-white/10 bg-sidebar-bg">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-primary-500/30 border-2 border-primary-400 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary-300">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-xs text-sidebar-muted capitalize">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 mt-1 rounded-lg
              text-sm font-medium bg-danger-500/20 text-danger-300 hover:bg-danger-500/30 hover:text-white
              transition-colors duration-150 border border-danger-400/30"
            aria-label="Sign out of your account"
          >
            <LogOut size={18} />
            <span>{t('auth.signOut')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="app-main-column flex-1 flex flex-col min-w-0 min-h-0 h-dvh">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-surface-200" role="banner">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-surface-100 transition-colors"
              aria-label="Open navigation menu"
            >
              <Menu size={22} className="text-text-secondary" />
            </button>

            {/* Application chrome stays intentionally neutral. Each route owns its page H1. */}
            <div className="flex-1 lg:hidden" aria-hidden="true" />

            {/* Spacer (desktop) */}
            <div className="hidden lg:block" />

            {/* Right side */}
            <div className="flex items-center gap-3">
              {user && (
                <>
                  <LanguageSwitcher />
                  <NotificationBell userId={user.uid} role={user.role} />

                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main id="main-content" className="app-main flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 lg:p-6" role="main">
          {children}
        </main>
      </div>
      {user && <MobileDock navItems={navItems} role={user.role} />}
      {user && incomingCall.state === 'ringing' && incomingCall.call && (
        <IncomingCallOverlay
          call={incomingCall.call}
          ringBlocked={incomingCall.ringBlocked}
          onAccept={incomingCall.accept}
          onDecline={incomingCall.decline}
          onEnableSound={incomingCall.enableSound}
          onSilence={incomingCall.silence}
        />
      )}
      <CookieConsent />
    </div>
  );
};

// ========== Protected Route ==========
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredPermission?: string;
}> = ({ children, allowedRoles, requiredPermission }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }

  return <Layout>{children}</Layout>;
};

// ========== Login Page ==========
const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Standalone email-verification path: when a registered-but-unverified user
  // tries to sign in, the server returns email_not_verified. Show an inline
  // OTP step here instead of stranding the user on the login form.
  const [verifyMode, setVerifyMode] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyOtp, setVerifyOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [info, setInfo] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err: any) {
      if (err?.code === 'email_not_verified') {
        setVerifyEmail((email || '').trim().toLowerCase());
        setVerifyMode(true);
        setInfo('Your email is not verified yet. Enter the 6-digit code sent to your inbox to continue, or request a new one.');
        return;
      }
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const res = await authApi.verifyEmail(verifyEmail, verifyOtp);
      if (res.user) {
        const userData: AuthUser = {
          uid: res.user.uid || res.user.id,
          name: res.user.name || 'User',
          role: res.user.role || 'patient',
          token: res.user.token || '',
        };
        localStorage.setItem('bhealth_user', JSON.stringify(userData));
        localStorage.setItem('token', userData.token);
        // Respect the /web basename (without it the browser lands on the
        // marketing site instead of the app dashboard).
        const base = window.location.pathname.startsWith('/web') ? '/web' : '';
        window.location.href = `${base}/${userData.role}/dashboard`;
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendVerify = async () => {
    if (resendCooldown > 0 || !verifyEmail) return;
    setError('');
    try {
      await authApi.resendOtp(verifyEmail);
      setInfo('A new code has been sent to your email.');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    }
  };

  // Cooldown timer for the resend button
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <div className="auth-shell">
      <section className="auth-brand" aria-hidden="true"><div className="auth-brand__content"><div className="auth-brand__eyebrow">BHABY Health Connect</div><h2 className="auth-brand__title">Care, connected with precision.</h2><p className="auth-brand__copy">A focused healthcare workspace for appointments, clinical coordination, laboratory care, payments, and trusted patient records.</p></div></section>
      <section className="auth-panel"><div className="w-full max-w-md animate-fade-in">
        <div className="card p-8">
          {/* Brand — official logo */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 shadow-sm">
              <BrandLogo className="w-full h-full" />
            </div>
            <h1 className="text-[1.7rem] font-extrabold tracking-tight text-text-primary leading-none">BHABY</h1>
            <p className="text-[0.68rem] font-bold text-primary-500 uppercase tracking-[0.2em] mt-2">Health Connect</p>
            <p className="text-sm text-text-muted mt-4">{t('app.tagline')}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-500 text-sm flex items-center gap-2" role="alert">
              <AlertTriangle size={14} className="flex-shrink-0" /> {error}
            </div>
          )}
          {info && !error && (
            <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-100 text-primary-700 text-sm flex items-center gap-2" role="status">
              <Mail size={14} className="flex-shrink-0" /> {info}
            </div>
          )}

          {verifyMode ? (
            /* Email verification step (reached when login returns email_not_verified) */
            <form onSubmit={handleVerify} noValidate>
              <div className="mb-2">
                <label htmlFor="verify-otp" className="label">Verification Code</label>
                <input
                  id="verify-otp"
                  type="text"
                  value={verifyOtp}
                  onChange={(e) => setVerifyOtp(e.target.value)}
                  placeholder="6-digit code"
                  className="input"
                  required
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-required="true"
                />
              </div>
              <div className="mb-6 text-right">
                <button
                  type="button"
                  onClick={handleResendVerify}
                  disabled={resendCooldown > 0}
                  className={`text-sm font-medium min-h-[44px] inline-flex items-center ${resendCooldown > 0 ? 'text-text-muted cursor-not-allowed' : 'text-primary-500 hover:text-primary-700'}`}
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={verifying} aria-busy={verifying}>
                {verifying ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </button>
              <button
                type="button"
                onClick={() => { setVerifyMode(false); setInfo(''); setError(''); }}
                className="w-full mt-3 text-sm text-text-muted hover:text-text-secondary min-h-[44px]"
              >
                Back to Sign In
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label htmlFor="email" className="label">{t('auth.email')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input"
                required
                autoComplete="email"
                aria-required="true"
              />
            </div>

            <div className="mb-2">
              <label htmlFor="password" className="label">{t('auth.password')}</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterPassword')}
                  className="input pr-11"
                  required
                  autoComplete="current-password"
                  aria-required="true"
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-surface-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff size={16} className="text-text-muted" />
                  ) : (
                    <Eye size={16} className="text-text-muted" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="mb-6 text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors min-h-[44px] inline-flex items-center gap-1"
                aria-label="Forgot your password?"
              >
                <KeyRound size={12} /> {t('auth.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.signIn')
              )}
            </button>
          </form>
          )}

          {/* Sign Up */}
          <div className="mt-6 pt-5 border-t border-surface-100 text-center">
            <p className="text-sm text-text-muted">
              {t('auth.noAccount')}{' '}
              <Link
                to="/register"
                className="text-primary-500 hover:text-primary-700 font-semibold transition-colors min-h-[44px] inline-flex items-center gap-1"
                aria-label="Request access or sign up"
              >
                <UserPlus size={12} /> {t('auth.createAccount')}
              </Link>
            </p>
            {/* Guest access — temporary verified identity for one care journey */}
            <p className="text-sm text-text-muted mt-2">
              <Link
                to="/guest"
                className="text-primary-500 hover:text-primary-700 font-semibold transition-colors min-h-[44px] inline-flex items-center gap-1"
                aria-label="Continue as guest"
              >
                <Clock size={12} /> {t('guestUi.title')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      </section>
    </div>
  );
};

// ========== Forgot Password Page ==========
const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp' | 'done'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authApi.forgotPassword(email);
      setSuccess('A reset code has been sent to your email.');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authApi.resetPassword(email, otp, newPassword);
      setSuccess('Password reset successful! Redirecting to login...');
      setStep('done');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <section className="auth-brand" aria-hidden="true"><div className="auth-brand__content"><div className="auth-brand__eyebrow">BHABY Health Connect</div><h2 className="auth-brand__title">One care journey. One trusted place.</h2><p className="auth-brand__copy">Create a secure account and keep the next step in care clear, fast, and human.</p></div></section>
      <section className="auth-panel">
      <div className="w-full max-w-md animate-fade-in">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-500 flex items-center justify-center mb-4 shadow-sm">
              <KeyRound size={26} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-primary-500">Reset Password</h1>
            <p className="text-sm text-text-muted mt-1">{step === 'email' ? 'Enter your email to receive a reset code' : step === 'otp' ? 'Enter the code and your new password' : 'All done!'}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-500 text-sm flex items-center gap-2" role="alert">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          {success && step !== 'done' && (
            <div className="mb-4 p-3 rounded-lg bg-success-50 border border-success-100 text-success-500 text-sm flex items-center gap-2" role="status">
              <CheckCircle size={14} /> {success}
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleSendCode}>
              <div className="mb-6">
                <label htmlFor="reset-email" className="label">Email address</label>
                <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="input" required aria-required="true" />
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Mail size={16} /> Send Reset Code</>}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleReset}>
              <div className="mb-4">
                <label htmlFor="reset-otp" className="label">Reset Code</label>
                <input id="reset-otp" type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="input" required aria-required="true" maxLength={6} />
              </div>
              <div className="mb-6">
                <label htmlFor="reset-new-pw" className="label">New Password</label>
                <div className="relative">
                  <input id="reset-new-pw" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="input pr-11" aria-required="true" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-surface-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={showPassword ? 'Hide password' : 'Show password'} tabIndex={-1}>
                    {showPassword ? <EyeOff size={16} className="text-text-muted" /> : <Eye size={16} className="text-text-muted" />}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? <><Loader2 size={18} className="animate-spin" /> Resetting...</> : 'Reset Password'}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <CheckCircle size={48} className="mx-auto mb-3 text-success-500" />
              <p className="text-sm text-text-muted">{success}</p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-500 hover:text-primary-700 font-medium">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
      </section>
    </div>
  );
};

// ========== Register Page ==========
const RegisterPage: React.FC = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('patient');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) { setError('Please enter your full name.'); return; }
    if (!trimmedEmail) { setError('Please enter your email address.'); return; }
    if (!password) { setError('Please enter a password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await authApi.register({ name: trimmedName, email: trimmedEmail, password, role });
      setSuccess('Account created! Check your email for the verification code.');
      setOtpStep(true);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await authApi.verifyEmail(email, otp);
      if (res.user) {
        const userData: AuthUser = {
          uid: res.user.uid || res.user.id,
          name: res.user.name || name,
          role: res.user.role || 'patient',
          token: res.user.token || '',
        };
        localStorage.setItem('bhealth_user', JSON.stringify(userData));
        localStorage.setItem('token', userData.token);
        // Clean navigation — SPA re-reads auth from localStorage on load.
        // Respect the /web basename (without it the browser lands on the
        // marketing site instead of the app dashboard).
        const base = window.location.pathname.startsWith('/web') ? '/web' : '';
        window.location.href = `${base}/${userData.role}/dashboard`;
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authApi.resendOtp(email);
      setSuccess('New code sent to your email.');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    }
  };

  // Cooldown timer
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 shadow-sm">
              <BrandLogo className="w-full h-full" />
            </div>
            <h1 className="text-2xl font-bold text-primary-500">Create Account</h1>
            <p className="text-sm text-text-muted mt-1">{otpStep ? 'Verify your email' : t('registerUi.joinTagline')}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-500 text-sm flex items-center gap-2" role="alert">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-lg bg-success-50 border border-success-100 text-success-500 text-sm flex items-center gap-2" role="status">
              <CheckCircle size={14} /> {success}
            </div>
          )}

          {!otpStep ? (
            <form onSubmit={handleRegister}>
              <div className="mb-4">
                <label htmlFor="reg-name" className="label">Full Name</label>
                <input id="reg-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" className="input" aria-required="true" />
              </div>
              <div className="mb-4">
                <label htmlFor="reg-email" className="label">Email address</label>
                <input id="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="input" required aria-required="true" />
              </div>
              <div className="mb-4">
                <label htmlFor="reg-password" className="label">Password</label>
                <div className="relative">
                  <input id="reg-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="input pr-11" aria-required="true" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-surface-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={showPassword ? 'Hide password' : 'Show password'} tabIndex={-1}>
                    {showPassword ? <EyeOff size={16} className="text-text-muted" /> : <Eye size={16} className="text-text-muted" />}
                  </button>
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="reg-role" className="label">I am a</label>
                <select id="reg-role" value={role} onChange={(e) => setRole(e.target.value)} className="input">
                  <option value="patient">Patient</option>
                  <option value="doctor">Doctor</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? <><Loader2 size={18} className="animate-spin" /> Creating account...</> : 'Create Account'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <div className="mb-2">
                <label htmlFor="reg-otp" className="label">Verification Code</label>
                <input id="reg-otp" type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="input" required aria-required="true" maxLength={6} />
              </div>
              <div className="mb-6 text-right">
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0} className={`text-sm font-medium ${resendCooldown > 0 ? 'text-text-muted cursor-not-allowed' : 'text-primary-500 hover:text-primary-700'}`}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? <><Loader2 size={18} className="animate-spin" /> Verifying...</> : 'Verify Email'}
              </button>
            </form>
          )}

          <div className="mt-6 pt-5 border-t border-surface-100 text-center">
            <p className="text-sm text-text-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-500 hover:text-primary-700 font-semibold transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Placeholder: Dashboard Components (imported from separate files) ==========
// These are lazy-loaded for code splitting
const PatientDashboard = React.lazy(() => import('./components/patient/PatientDashboard'));
const PatientBookings = React.lazy(() => import('./components/patient/BookingList'));
const RequestBooking = React.lazy(() => import('./components/patient/RequestBooking'));
const GuestPage = React.lazy(() => import('./components/patient/GuestPage'));
const PatientProfile = React.lazy(() => import('./components/patient/PatientProfile'));
const DoctorDashboard = React.lazy(() => import('./components/doctor/DoctorDashboard'));
const DoctorPendingBookings = React.lazy(() => import('./components/doctor/DoctorPendingBookings'));
const DoctorConsultations = React.lazy(() => import('./components/doctor/DoctorConsultations'));
const DoctorPatients = React.lazy(() => import('./components/doctor/DoctorPatients'));
const ConsultationForm = React.lazy(() => import('./components/doctor/ConsultationForm'));
const VideoConsultationRoom = React.lazy(() => import('./components/doctor/VideoConsultationRoom'));
const PatientDetailView = React.lazy(() => import('./components/doctor/PatientDetailView'));
const DoctorLabTests = React.lazy(() => import('./components/doctor/DoctorLabTests'));
const DoctorWallet = React.lazy(() => import('./components/doctor/DoctorWallet'));
const NurseDashboard = React.lazy(() => import('./components/nurse/NurseDashboard'));
const NurseTasks = React.lazy(() => import('./components/nurse/NurseTasks'));
const NurseCollections = React.lazy(() => import('./components/nurse/NurseCollections'));
const SampleCollectionWorkflow = React.lazy(() => import('./components/nurse/SampleCollectionWorkflow'));
const PatientCheckAssessment = React.lazy(() => import('./components/nurse/PatientCheckAssessment'));
const MedicationAdministration = React.lazy(() => import('./components/nurse/MedicationAdministration'));
const NurseTaskManagement = React.lazy(() => import('./components/nurse/NurseTaskManagement'));
const FindDoctor = React.lazy(() => import('./components/patient/FindDoctor'));
const DoctorProfile = React.lazy(() => import('./components/patient/DoctorProfile'));
const AppointmentDetails = React.lazy(() => import('./components/patient/AppointmentDetails'));
const MedicalHistory = React.lazy(() => import('./components/patient/MedicalHistory'));
const LabTestCatalog = React.lazy(() => import('./components/patient/LabTestCatalog'));
const PaymentHistory = React.lazy(() => import('./components/patient/PaymentHistory'));
const DoctorSchedule = React.lazy(() => import('./components/doctor/DoctorSchedule'));
const DoctorProfileSetup = React.lazy(() => import('./components/doctor/DoctorProfileSetup'));
const DoctorPatientTransfer = React.lazy(() => import('./components/doctor/DoctorPatientTransfer'));
const PatientConsultationRoom = React.lazy(() => import('./components/patient/PatientConsultationRoom'));
const PatientPrescriptions = React.lazy(() => import('./components/patient/PatientPrescriptions'));
const LabTestDetail = React.lazy(() => import('./components/patient/LabTestDetail'));
const NursePatientHistory = React.lazy(() => import('./components/nurse/NursePatientHistory'));
const NurseProfileShift = React.lazy(() => import('./components/nurse/NurseProfileShift'));
const AdminDoctorVerification = React.lazy(() => import('./components/admin/AdminDoctorReview'));
const AdminLabOrders = React.lazy(() => import('./components/admin/AdminLabOrders'));
const AdminClinicalReview = React.lazy(() => import('./components/admin/AdminClinicalReview'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const AdminBookings = React.lazy(() => import('./components/admin/AdminBookings'));
const PricingDashboard = React.lazy(() => import('./components/admin/PricingDashboard'));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement'));
const EmergencyMonitoring = React.lazy(() => import('./components/admin/EmergencyMonitoring'));
const AdminPayments = React.lazy(() => import('./components/admin/AdminPayments'));
const AdminInsuranceReview = React.lazy(() => import('./components/admin/AdminInsuranceReview'));
const AdminWithdrawals = React.lazy(() => import('./components/admin/AdminWithdrawals'));
const AdminNurseTasks = React.lazy(() => import('./components/admin/AdminNurseTasks'));
const AdminRecordsAccess = React.lazy(() => import('./components/admin/AdminRecordsAccess'));
const AdminNurseVerification = React.lazy(() => import('./components/admin/AdminNurseVerification'));
const AdminMonitoring = React.lazy(() => import('./components/admin/AdminMonitoring'));
const AdminFeedbackPage = React.lazy(() => import('./components/admin/AdminFeedbackPage'));
const NotificationInbox = React.lazy(() => import('./components/shared/NotificationInbox'));
const PrivacyPolicy = React.lazy(() => import('./components/shared/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/shared/TermsOfService'));
const PatientLabResults = React.lazy(() => import('./components/patient/PatientLabResults'));
const RecordsAccessRequest = React.lazy(() => import('./components/patient/RecordsAccessRequest'));
const DoctorMessaging = React.lazy(() => import('./components/doctor/DoctorMessaging'));

// Loading fallback
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center py-20" role="status" aria-label="Loading page">
    <Loader2 size={32} className="animate-spin text-primary-500" />
    <span className="ml-3 text-text-muted text-sm">Loading...</span>
  </div>
);

// ========== Main App ==========
const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem('bhealth_user');
    return stored ? JSON.parse(stored) : null;
  });

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    const userData: AuthUser = {
      uid: res.uid || res.user?.uid || 'unknown',
      name: res.name || res.user?.name || 'User',
      role: res.role || res.user?.role || 'patient',
      token: res.token || res.user?.token || '',
    };
    localStorage.setItem('bhealth_user', JSON.stringify(userData));
    localStorage.setItem('token', userData.token);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('bhealth_user');
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  const getDefaultRoute = () => {
    if (!user) return '/login';
    return `/${user.role}/dashboard`;
  };

  return (
    <LanguageProvider>
    <ErrorBoundary>
    <PermissionProvider>
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      <React.Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/register" element={user ? <Navigate to={getDefaultRoute()} replace /> : <RegisterPage />} />
          <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRoute()} replace /> : <ForgotPasswordPage />} />

          {/* Guest access — temporary verified identity for one care journey */}
          <Route path="/guest" element={user ? <Navigate to={getDefaultRoute()} replace /> : <GuestPage />} />

          {/* Patient Routes */}
          <Route path="/patient/dashboard" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientDashboard /></ProtectedRoute>
          } />
          <Route path="/patient/bookings" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientBookings /></ProtectedRoute>
          } />
          <Route path="/patient/book" element={
            <ProtectedRoute allowedRoles={['patient']}><RequestBooking /></ProtectedRoute>
          } />
          <Route path="/patient/profile" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientProfile /></ProtectedRoute>
          } />
          <Route path="/patient/lab-results" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientLabResults /></ProtectedRoute>
          } />
          <Route path="/patient/find-doctor" element={
            <ProtectedRoute allowedRoles={['patient']}><FindDoctor /></ProtectedRoute>
          } />
          <Route path="/patient/doctor/:doctorId" element={
            <ProtectedRoute allowedRoles={['patient']}><DoctorProfile /></ProtectedRoute>
          } />
          <Route path="/patient/appointment/:bookingId" element={
            <ProtectedRoute allowedRoles={['patient']}><AppointmentDetails /></ProtectedRoute>
          } />
          <Route path="/patient/medical-history" element={
            <ProtectedRoute allowedRoles={['patient']}><MedicalHistory /></ProtectedRoute>
          } />
          <Route path="/patient/lab-catalog" element={
            <ProtectedRoute allowedRoles={['patient']}><LabTestCatalog /></ProtectedRoute>
          } />
          <Route path="/patient/payments" element={
            <ProtectedRoute allowedRoles={['patient']}><PaymentHistory /></ProtectedRoute>
          } />
          <Route path="/patient/video/:bookingId" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientConsultationRoom /></ProtectedRoute>
          } />
          <Route path="/patient/prescriptions" element={
            <ProtectedRoute allowedRoles={['patient']}><PatientPrescriptions /></ProtectedRoute>
          } />
          <Route path="/patient/lab-test/:testId" element={
            <ProtectedRoute allowedRoles={['patient']}><LabTestDetail /></ProtectedRoute>
          } />
          <Route path="/patient/records-access" element={
            <ProtectedRoute allowedRoles={['patient']}><RecordsAccessRequest /></ProtectedRoute>
          } />

          {/* Doctor Routes */}
          <Route path="/doctor/dashboard" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorVerificationGate><DoctorDashboard /></DoctorVerificationGate></ProtectedRoute>
          } />
          <Route path="/doctor/pending" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorVerificationGate><DoctorPendingBookings /></DoctorVerificationGate></ProtectedRoute>
          } />
          <Route path="/doctor/consultations" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorConsultations /></ProtectedRoute>
          } />
          <Route path="/doctor/consultation/:bookingId" element={
            <ProtectedRoute allowedRoles={['doctor']}><ConsultationForm /></ProtectedRoute>
          } />
          <Route path="/doctor/video/:bookingId" element={
            <ProtectedRoute allowedRoles={['doctor']}><VideoConsultationRoom /></ProtectedRoute>
          } />
          <Route path="/doctor/patients" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorPatients /></ProtectedRoute>
          } />
          <Route path="/doctor/patient/:patientId" element={
            <ProtectedRoute allowedRoles={['doctor']}><PatientDetailView /></ProtectedRoute>
          } />
          <Route path="/doctor/lab-tests" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorLabTests /></ProtectedRoute>
          } />
          <Route path="/doctor/wallet" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorWallet /></ProtectedRoute>
          } />
          <Route path="/doctor/schedule" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorSchedule /></ProtectedRoute>
          } />
          <Route path="/doctor/profile" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorProfileSetup /></ProtectedRoute>
          } />
          <Route path="/doctor/messages" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorMessaging /></ProtectedRoute>
          } />
          <Route path="/doctor/patient-transfer" element={
            <ProtectedRoute allowedRoles={['doctor']}><DoctorPatientTransfer /></ProtectedRoute>
          } />


          {/* Nurse Routes */}
          <Route path="/nurse/dashboard" element={
            <ProtectedRoute allowedRoles={['nurse']}><NurseDashboard /></ProtectedRoute>
          } />
          <Route path="/nurse/tasks" element={
            <ProtectedRoute allowedRoles={['nurse']}><NurseTasks /></ProtectedRoute>
          } />
          <Route path="/nurse/collections/:taskId?" element={
            <ProtectedRoute allowedRoles={['nurse']}><NurseCollections /></ProtectedRoute>
          } />
          <Route path="/nurse/sample/:taskId" element={
            <ProtectedRoute allowedRoles={['nurse']}><SampleCollectionWorkflow /></ProtectedRoute>
          } />
          <Route path="/nurse/patient-check/:taskId" element={
            <ProtectedRoute allowedRoles={['nurse']}><PatientCheckAssessment /></ProtectedRoute>
          } />
          <Route path="/nurse/medication" element={
            <ProtectedRoute allowedRoles={['nurse']}><MedicationAdministration /></ProtectedRoute>
          } />
          <Route path="/nurse/task-management" element={
            <ProtectedRoute allowedRoles={['nurse']}><NurseTaskManagement /></ProtectedRoute>
          } />
          <Route path="/nurse/patient-history/:patientId" element={
            <ProtectedRoute allowedRoles={['nurse']}><NursePatientHistory /></ProtectedRoute>
          } />
          <Route path="/nurse/profile" element={
            <ProtectedRoute allowedRoles={['nurse']}><NurseProfileShift /></ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/bookings" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminBookings /></ProtectedRoute>
          } />
          <Route path="/admin/pricing" element={
            <ProtectedRoute allowedRoles={['admin']}><PricingDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>
          } />
          <Route path="/admin/insurance" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminInsuranceReview /></ProtectedRoute>
          } />
          <Route path="/admin/emergency" element={
            <ProtectedRoute allowedRoles={['admin']}><EmergencyMonitoring /></ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminPayments /></ProtectedRoute>
          } />
          <Route path="/admin/withdrawals" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminWithdrawals /></ProtectedRoute>
          } />
          <Route path="/admin/nurse-tasks" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminNurseTasks /></ProtectedRoute>
          } />
          <Route path="/admin/feedback" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminFeedbackPage /></ProtectedRoute>
          } />
          <Route path="/admin/monitoring" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminMonitoring /></ProtectedRoute>
          } />
          <Route path="/admin/doctor-verification" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminDoctorVerification /></ProtectedRoute>
          } />
          <Route path="/admin/nurse-verification" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminNurseVerification /></ProtectedRoute>
          } />
          <Route path="/admin/lab-orders" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminLabOrders /></ProtectedRoute>
          } />
          <Route path="/admin/records-access" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminRecordsAccess /></ProtectedRoute>
          } />
          <Route path="/admin/clinical-review" element={
            <ProtectedRoute allowedRoles={['admin']}><AdminClinicalReview /></ProtectedRoute>
          } />

          {/* Shared Routes */}
          <Route path="/notifications" element={
            <ProtectedRoute><NotificationInbox /></ProtectedRoute>
          } />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
        </Routes>
      </React.Suspense>
    </AuthContext.Provider>
    </PermissionProvider>
    </ErrorBoundary>
    </LanguageProvider>
  );
};

export default App;
